'use client';

/**
 * app/error.tsx — route-level error boundary
 *
 * Handles errors thrown inside route segments. For the root-layout chunk
 * failure specifically, see app/global-error.tsx (which wraps the layout
 * itself). This file handles all sub-layout route errors.
 *
 * ChunkLoadError handling:
 *   A ChunkLoadError fires when a lazy-loaded route chunk is no longer
 *   available in S3/CloudFront after a deployment (the hash changed). We
 *   auto-reload once (tracked by sessionStorage) to pick up the new HTML
 *   referencing the current chunk hashes. If the reload fails again, the
 *   user sees a manual-reload prompt instead of an infinite loop.
 */

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { trackAppEvent } from '@/lib/posthog';

const CHUNK_RELOAD_FLAG = 'dc.chunkReloadAttempted';
// A reload that didn't fix the chunk error will cycle back within a few
// seconds. If the flag is older than this, the reload succeeded and a new
// chunk error should earn another automatic retry.
const CHUNK_RELOAD_TTL_MS = 15_000;

function isChunkLoadError(error: Error): boolean {
  return (
    error.name === 'ChunkLoadError' ||
    /loading chunk \d+ failed/i.test(error.message) ||
    /loading css chunk \d+ failed/i.test(error.message) ||
    /failed to fetch dynamically imported module/i.test(error.message)
  );
}

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();
  const isChunk = isChunkLoadError(error);
  const [reloadBlocked, setReloadBlocked] = useState(false);

  useEffect(() => {
    try {
      trackAppEvent('client_error_boundary_triggered', {
        error_name: error?.name || 'unknown',
        is_chunk_error: isChunk,
        route: pathname || undefined,
      });
    } catch {
      // never let analytics break the error UI
    }
  }, [error, isChunk, pathname]);

  useEffect(() => {
    if (!isChunk) return;

    try {
      const storedAt = sessionStorage.getItem(CHUNK_RELOAD_FLAG);
      const isRecent =
        storedAt !== null && Date.now() - Number(storedAt) < CHUNK_RELOAD_TTL_MS;
      if (!isRecent) {
        sessionStorage.setItem(CHUNK_RELOAD_FLAG, String(Date.now()));
        window.location.reload();
        return;
      }
    } catch {
      // sessionStorage unavailable
    }

    setReloadBlocked(true);
  }, [isChunk]);

  const handleManualReload = () => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
    } catch {}
    window.location.reload();
  };

  if (isChunk && !reloadBlocked) {
    return (
      <section className="bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-screen-xl px-4 py-8 lg:px-6 lg:py-16">
          <div className="mx-auto max-w-screen-sm text-center">
            <div className="mb-6 text-6xl">🔄</div>
            <p className="mb-2 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              Updating Dream Connect…
            </p>
            <p className="text-gray-500 dark:text-gray-400">
              A new version was deployed. Reloading automatically.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (isChunk && reloadBlocked) {
    return (
      <section className="bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-screen-xl px-4 py-8 lg:px-6 lg:py-16">
          <div className="mx-auto max-w-screen-sm text-center">
            <p className="mb-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Could not load this page
            </p>
            <p className="mb-6 text-gray-500 dark:text-gray-400">
              A required asset could not be loaded. Try a manual reload, or
              clear your browser cache.
            </p>
            <div className="flex justify-center gap-4">
              <button
                className="my-4 rounded-lg bg-purple-600 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-300 dark:bg-purple-700 dark:hover:bg-purple-800 dark:focus:ring-purple-900"
                onClick={handleManualReload}
                type="button"
              >
                Reload page
              </button>
              <Link href="/">
                <button
                  className="my-4 rounded-lg bg-purple-600 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-300 dark:bg-purple-700 dark:hover:bg-purple-800 dark:focus:ring-purple-900"
                  type="button"
                >
                  Back to Homepage
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white dark:bg-gray-900">
      <div className="mx-auto max-w-screen-xl px-4 py-8 lg:px-6 lg:py-16">
        <div className="mx-auto max-w-screen-sm text-center">
          <h1 className="mb-4 text-7xl font-extrabold tracking-tight text-white lg:text-9xl">
            500
          </h1>
          <p className="mb-4 text-3xl font-bold tracking-tight text-gray-900 dark:text-white md:text-4xl">
            Something went wrong!
          </p>
          <p className="mb-4 text-lg font-light text-gray-500 dark:text-gray-400">
            We are sorry for the inconvenience. Please try again later.
          </p>
          <div className="flex justify-center gap-4">
            <button
              className="my-4 rounded-lg bg-purple-600 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-300 dark:bg-purple-700 dark:hover:bg-purple-800 dark:focus:ring-purple-900"
              onClick={reset}
              type="button"
            >
              Try again
            </button>
            <Link href="/">
              <button
                className="my-4 rounded-lg bg-purple-600 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-4 focus:ring-purple-300 dark:bg-purple-700 dark:hover:bg-purple-800 dark:focus:ring-purple-900"
                type="button"
              >
                Back to Homepage
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
